<template>
    <Layout>
        <div id="page">
            <!-- Header Section Redesenhado -->
            <div class="mentee-header-card">
                <div class="mentee-profile-section">
                    <div class="mentee-avatar-container">
                        <b-skeleton v-show="!loadedImg" type="avatar" height="80px" width="80px"></b-skeleton>
                        <img v-show="loadedImg" @load="loadedImg=true" :src="`${env.url}mentees/image/${mentee.id}`"
                             alt="Avatar do mentorado" class="mentee-avatar">
                        <div class="mentee-status-indicator" :class="getStatusClass()"></div>
                    </div>
                    
                    <div class="mentee-info-section">
                        <div class="mentee-name-section">
                            <h1 class="mentee-name">{{ mentee.name || 'Carregando...' }}</h1>
                            <div class="mentee-badges">
                                <span class="badge badge-primary" v-if="mentee.profession">{{ mentee.profession }}</span>
                                <span class="badge badge-success" v-if="completed > 0">{{ completed }} tarefas concluídas</span>
                            </div>
                        </div>
                        
                        <div class="mentee-contact-info" v-if="Object.keys(mentee).length > 0">
                            <div class="contact-item" v-if="mentee.email">
                                <i class="fas fa-envelope"></i>
                                <span>{{ mentee.email }}</span>
                            </div>
                            <div class="contact-item" v-if="mentee.profession">
                                <i class="fas fa-briefcase"></i>
                                <span>{{ mentee.profession }}</span>
                            </div>
                        </div>
                        
                        <div class="mentee-social-links" v-if="hasSocialLinks()">
                            <a v-if="mentee.instagram" target="_blank" :href="'https://www.instagram.com/' + mentee.instagram" 
                               class="social-link instagram" title="Instagram">
                                <i class="fab fa-instagram"></i>
                            </a>
                            <a v-if="mentee.linkedin" target="_blank" :href="'https://www.linkedin.com/in/' + mentee.linkedin" 
                               class="social-link linkedin" title="LinkedIn">
                                <i class="fab fa-linkedin"></i>
                            </a>
                            <a v-if="mentee.youtube" target="_blank" :href="'https://www.youtube.com/' + mentee.youtube" 
                               class="social-link youtube" title="YouTube">
                                <i class="fab fa-youtube"></i>
                            </a>
                        </div>
                    </div>
                    
                    <div class="mentee-actions">
                        <router-link v-if="mentee.id" :to="'/mensagens/' + mentee.id" class="btn btn-primary btn-message">
                            <i class="fas fa-comment-alt"></i>
                            <span>Enviar Mensagem</span>
                        </router-link>
                    </div>
                </div>
                
                <div class="mentee-description" v-if="mentee.description">
                    <p>{{ mentee.description }}</p>
                </div>
            </div>

            <!-- Métricas de Progresso -->
            <div class="progress-metrics-section">
                    <div class="row">
                    <div class="col-md-3 col-sm-6 mb-3">
                        <div class="metric-card">
                            <div class="metric-icon">
                                <i class="fas fa-chart-line"></i>
                            </div>
                            <div class="metric-content">
                                <h3>{{ Math.round((completed / total) * 100) || 0 }}%</h3>
                                <p>Progresso Geral</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3 col-sm-6 mb-3">
                        <div class="metric-card">
                            <div class="metric-icon">
                                <i class="fas fa-tasks"></i>
                            </div>
                            <div class="metric-content">
                                <h3>{{ completed }}</h3>
                                <p>Tarefas Concluídas</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3 col-sm-6 mb-3">
                        <div class="metric-card">
                            <div class="metric-icon">
                                <i class="fas fa-clock"></i>
                            </div>
                            <div class="metric-content">
                                <h3>{{ total - completed }}</h3>
                                <p>Tarefas Pendentes</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-3 col-sm-6 mb-3">
                        <div class="metric-card">
                            <div class="metric-icon">
                                <i class="fas fa-calendar-check"></i>
                            </div>
                            <div class="metric-content">
                                <h3>{{ getDaysActive() }}</h3>
                                <p>Dias Ativos</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Barra de Progresso Melhorada -->
            <div class="progress-section">
                <div class="progress-header">
                    <h4>Progresso da Mentoria</h4>
                    <span class="progress-percentage">{{ Math.round((completed / total) * 100) || 0 }}%</span>
                </div>
                <div class="progress progress-enhanced">
                    <div class="progress-bar progress-bar-striped progress-bar-animated" 
                         role="progressbar" 
                         :style="`width: ${(completed / total) * 100 || 0}%;`"
                         :aria-valuenow="Math.round((completed / total) * 100) || 0" 
                         aria-valuemin="0" 
                         aria-valuemax="100">
                    </div>
                </div>
            </div>

            <!-- Componente de Mentoria -->
            <div class="mentoring-section">
                <Mentoring
                    @refresh-graphic="getDataGraphic"
                    @view-activity="openActivityModal"
                />
            </div>

            <!-- Modal para Visualizar Atividade -->
            <b-modal 
                id="activity-modal" 
                size="lg" 
                :title="selectedActivity ? selectedActivity.contents.subject : ''"
                hide-footer
                centered
                scrollable
            >
                <div v-if="selectedActivity" class="activity-modal-content">
                    <div class="activity-header">
                        <div class="activity-meta">
                            <span class="activity-type" :class="getActivityTypeClass(selectedActivity.contents.type)">
                                <i :class="getActivityTypeIcon(selectedActivity.contents.type)"></i>
                                {{ getTaskTypeText(selectedActivity.contents.type) }}
                            </span>
                            <span class="activity-status" :class="getTaskStatusClass(selectedActivity.status)">
                                <i :class="getTaskStatusIcon(selectedActivity.status)"></i>
                                {{ getTaskStatusText(selectedActivity.status) }}
                            </span>
                        </div>
                        <div class="activity-date" v-if="selectedActivity.created_at">
                            <i class="fas fa-calendar"></i>
                            {{ formatDate(selectedActivity.created_at) }}
                        </div>
                    </div>
                    
                    <div class="activity-content">
                        <h5>Conteúdo da Atividade</h5>
                        <div class="content-body">
                            <!-- Exibir formulário completo se disponível -->
                            <div v-if="selectedActivity.contents.type === 3 && formDetails">
                                <div class="form-details">
                                    <h6 class="form-title">{{ formDetails.form.title }}</h6>
                                    <div class="form-questions">
                                        <div v-for="(question, index) in formDetails.form.questions" 
                                             :key="question.id" 
                                             class="form-question">
                                            <div class="question-header">
                                                <span class="question-number">{{ index + 1 }}.</span>
                                                <span class="question-text">{{ question.question }}</span>
                                            </div>
                                            <div class="question-type">
                                                <span class="badge badge-info">
                                                    {{ getQuestionTypeText(question.type) }}
                                                </span>
                                            </div>
                                            
                                            <!-- Exibir resposta do mentorado -->
                                            <div class="question-answer">
                                                <div class="answer-label">Resposta do Mentorado:</div>
                                                <div class="answer-content">
                                                    <div v-if="question.type === 3 && question.alternatives">
                                                        <!-- Múltipla escolha - mostrar apenas as selecionadas -->
                                                        <div class="selected-alternatives">
                                                            <div v-for="alternativeId in getSelectedAlternativesText(question.id)" 
                                                                 :key="alternativeId"
                                                                 class="selected-alternative">
                                                                <span class="selected-letter">{{ String.fromCharCode(65 + question.alternatives.findIndex(alt => alt.id === alternativeId)) }}.</span>
                                                                <span class="selected-text">{{ question.alternatives.find(alt => alt.id === alternativeId)?.alternative || 'Alternativa não encontrada' }}</span>
                                                            </div>
                                                            <div v-if="getSelectedAlternativesText(question.id).length === 0" class="no-answer">
                                                                <i class="fas fa-exclamation-triangle"></i>
                                                                Nenhuma alternativa selecionada
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-else-if="formDetails.answers[question.id]" class="text-answer">
                                                        {{ formDetails.answers[question.id] }}
                                                    </div>
                                                    <div v-else class="no-answer">
                                                        <i class="fas fa-exclamation-triangle"></i>
                                                        Nenhuma resposta fornecida
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Exibir conteúdo padrão se não for formulário ou se não carregou -->
                            <div v-else-if="getActivityContent(selectedActivity)">
                                <div v-html="getActivityContent(selectedActivity)"></div>
                            </div>
                            <div v-else class="no-content">
                                <i class="fas fa-info-circle"></i>
                                <p>Nenhum conteúdo adicional disponível para esta atividade.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="activity-actions">
                        <button class="btn btn-secondary" @click="$bvModal.hide('activity-modal')">
                            <i class="fas fa-times"></i>
                            Fechar
                        </button>
                        <button 
                            v-if="selectedActivity.status === 2" 
                            class="btn btn-primary"
                            @click="viewFullActivity"
                        >
                            <i class="fas fa-external-link-alt"></i>
                            Ver Detalhes Completos
                        </button>
                    </div>
                </div>
            </b-modal>
        </div>
    </Layout>
</template>

<script>

import Layout from "@/components/layout/main.vue";
import Mentoring from "@/views/mentee/mentoring.vue";
import http from '@/http';
import env from '@/env'
import {endLoading, startLoading} from "@/components/composables/spinners";

export default {

    components: {
        Layout,
        Mentoring,
    },

    data() {
        return {
            mentee: {},
            completed: 0,
            total: 1,
            env,
            loadedImg: false,
            startDate: null,
            selectedActivity: null,
            formDetails: null
        }
    },
    methods: {
        getData() {
            this.$store.commit('api/setSpinner', true);
            startLoading('page');

            const id = this.$route.params.mentee;

            http.get('mentees/data/' + id)
                .then(response => {
                    this.mentee = response.data.data[0];
                    this.$store.state.mentee.mentee = this.mentee;
                })
                .finally(() => {
                    this.$store.commit('api/setSpinner', false);
                    endLoading('page');
                })
        },

        getDataGraphic() {
            const formData = {
                mentee: this.$route.params.mentee,
                id: this.$route.params.mentoring
            }
            http.post('tasks/completed-by-mentee', formData)
                .then(response => {
                    this.completed = response.data.completed;
                    this.total = response.data.total;
                })
                .catch(e => {
                    console.error('Get Data Tasks Mentoring: ', e)
                })
        },

        getStatusClass() {
            const progress = (this.completed / this.total) * 100;
            if (progress >= 80) return 'status-excellent';
            if (progress >= 60) return 'status-good';
            if (progress >= 40) return 'status-average';
            return 'status-poor';
        },

        hasSocialLinks() {
            return this.mentee.instagram || this.mentee.linkedin || this.mentee.youtube;
        },

        getDaysActive() {
            if (!this.startDate) return 0;
            const today = new Date();
            const start = new Date(this.startDate);
            const diffTime = Math.abs(today - start);
            return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        },

        async openActivityModal(activity) {
            this.selectedActivity = activity;
            this.formDetails = null;
            
            // Se for um formulário, carregar os detalhes
            if (activity.contents.type === 3 && activity.contents.form_id) {
                this.formDetails = await this.loadFormDetails(activity.contents.form_id);
            }
            
            this.$bvModal.show('activity-modal');
        },

        getActivityTypeClass(type) {
            const classes = {
                3: 'type-form',
                4: 'type-activity',
                5: 'type-schedule'
            };
            return classes[type] || 'type-default';
        },

        getActivityTypeIcon(type) {
            const icons = {
                3: 'fas fa-file-alt',
                4: 'fas fa-tasks',
                5: 'fas fa-calendar-alt'
            };
            return icons[type] || 'fas fa-circle';
        },

        getTaskTypeText(type) {
            const types = {
                3: 'Formulário',
                4: 'Atividade',
                5: 'Agenda'
            };
            return types[type] || 'Tarefa';
        },

        getTaskStatusClass(status) {
            if (status === 3) return 'status-completed';
            if (status === 2) return 'status-pending';
            if (status === 1) return 'status-waiting';
            return 'status-waiting';
        },

        getTaskStatusIcon(status) {
            if (status === 3) return 'fas fa-check-circle';
            if (status === 2) return 'fas fa-exclamation-circle';
            if (status === 1) return 'fas fa-clock';
            return 'fas fa-clock';
        },

        getTaskStatusText(status) {
            if (status === 3) return 'Concluído';
            if (status === 2) return 'Pendente FeedBack Mentor';
            if (status === 1) return 'Aguardando';
            return 'Aguardando';
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR');
        },

        viewFullActivity() {
            if (!this.selectedActivity) return;
            
            let url = null;
            if (this.selectedActivity.contents.type === 3) {
                url = `/mentorias/detalhes/${this.$route.params.mentoring}/mentorando/${this.$route.params.mentee}/formulario/${this.selectedActivity.contents.id}/${this.selectedActivity.contents.form_id}`;
            } else if (this.selectedActivity.contents.type === 4) {
                url = `/mentorias/detalhes/${this.$route.params.mentoring}/mentorando/${this.$route.params.mentee}/atividade/${this.selectedActivity.contents.id}`;
            } else if (this.selectedActivity.contents.type === 5) {
                url = '/agenda';
            }

            if (url) {
                this.$bvModal.hide('activity-modal');
                this.$router.push(url);
            }
        },

        getActivityContent(activity) {
            if (!activity || !activity.contents) return null;
            
            // Para atividades (type 4), buscar na description da activity
            if (activity.contents.type === 4 && activity.contents.activity) {
                return activity.contents.activity.description;
            }
            
            // Para formulários (type 3), buscar no subject
            if (activity.contents.type === 3) {
                return activity.contents.subject;
            }
            
            // Para outros tipos, tentar diferentes propriedades
            const content = activity.contents.description || 
                           activity.contents.subject ||
                           activity.contents.content || 
                           activity.contents.text ||
                           activity.contents.question ||
                           activity.contents.answer;
            
            return content;
        },

        async loadFormDetails(formId) {
            if (!formId) return null;
            
            try {
                // Buscar formulário com respostas do mentorado
                const response = await http.post('/forms/mentee', {
                    form_id: formId,
                    mentee_id: this.$route.params.mentee,
                    content_id: this.selectedActivity.contents.id
                });
                return response.data;
            } catch (error) {
                console.error('Erro ao carregar formulário:', error);
                return null;
            }
        },

        getQuestionTypeText(type) {
            const types = {
                1: 'Texto Livre',
                2: 'Escala',
                3: 'Múltipla Escolha',
                4: 'Data',
                5: 'Número'
            };
            return types[type] || 'Desconhecido';
        },

        isAlternativeSelected(questionId, alternativeId) {
            if (!this.formDetails || !this.formDetails.answers) return false;
            const answer = this.formDetails.answers[questionId];
            if (Array.isArray(answer)) {
                return answer.includes(alternativeId);
            }
            return answer === alternativeId;
        },

        getSelectedAlternativesText(questionId) {
            if (!this.formDetails || !this.formDetails.answers) return [];
            const answer = this.formDetails.answers[questionId];
            if (!answer) return [];
            
            // Se for array (múltipla escolha), retornar os IDs selecionados
            if (Array.isArray(answer)) {
                return answer;
            }
            // Se for único, retornar como array
            return [answer];
        }
    },
    mounted() {
        this.getData();
        this.getDataGraphic();
    }
}

</script>

<style scoped>
/* Header Section */
.mentee-header-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 15px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    color: white;
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.mentee-profile-section {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    margin-bottom: 1rem;
}

.mentee-avatar-container {
    position: relative;
    flex-shrink: 0;
}

.mentee-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    border: 3px solid rgba(255,255,255,0.3);
    object-fit: cover;
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.mentee-status-indicator {
    position: absolute;
    bottom: 5px;
    right: 5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 3px solid white;
}

.status-excellent { background: #28a745; }
.status-good { background: #17a2b8; }
.status-average { background: #ffc107; }
.status-poor { background: #dc3545; }

.mentee-info-section {
    flex: 1;
    min-width: 0;
}

.mentee-name {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.75rem 0;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mentee-badges {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
}

.mentee-contact-info {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
}

.contact-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.95rem;
    opacity: 0.9;
}

.contact-item i {
    width: 16px;
    text-align: center;
}

.mentee-social-links {
    display: flex;
    gap: 1rem;
}

.social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    color: white;
    text-decoration: none;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.social-link:hover {
    background: rgba(255,255,255,0.3);
    transform: translateY(-2px);
    color: white;
    text-decoration: none;
}

.social-link.instagram:hover { background: #E4405F; }
.social-link.linkedin:hover { background: #0077B5; }
.social-link.youtube:hover { background: #FF0000; }

.mentee-actions {
    flex-shrink: 0;
}

.btn-message {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    background: rgba(255,255,255,0.2);
    border: 2px solid rgba(255,255,255,0.3);
    backdrop-filter: blur(10px);
}

.btn-message:hover {
    background: rgba(255,255,255,0.3);
    transform: translateY(-2px);
    color: white;
    text-decoration: none;
}

.mentee-description {
    background: rgba(255,255,255,0.1);
    border-radius: 10px;
    padding: 1rem;
    backdrop-filter: blur(10px);
}

.mentee-description p {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.6;
    opacity: 0.95;
}

/* Métricas de Progresso */
.progress-metrics-section {
    margin-bottom: 2rem;
}

.metric-card {
    background: white;
    border-radius: 15px;
    padding: 1.5rem;
    box-shadow: 0 5px 20px rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.05);
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.metric-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
}

.metric-icon {
    width: 60px;
    height: 60px;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
    flex-shrink: 0;
}

.metric-card:nth-child(1) .metric-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
.metric-card:nth-child(2) .metric-icon { background: linear-gradient(135deg, #f093fb, #f5576c); }
.metric-card:nth-child(3) .metric-icon { background: linear-gradient(135deg, #4facfe, #00f2fe); }
.metric-card:nth-child(4) .metric-icon { background: linear-gradient(135deg, #43e97b, #38f9d7); }

.metric-content h3 {
    font-size: 2rem;
    font-weight: 700;
    margin: 0 0 0.25rem 0;
    color: #2c3e50;
}

.metric-content p {
    margin: 0;
    color: #7f8c8d;
    font-size: 0.9rem;
    font-weight: 500;
}

/* Barra de Progresso */
.progress-section {
    background: white;
    border-radius: 15px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 5px 20px rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.05);
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.progress-header h4 {
    margin: 0;
    color: #2c3e50;
    font-weight: 600;
}

.progress-percentage {
    font-size: 1.5rem;
    font-weight: 700;
    color: #667eea;
}

.progress-enhanced {
    height: 20px;
    border-radius: 10px;
    background: #f8f9fa;
    overflow: hidden;
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
}

.progress-enhanced .progress-bar {
    background: linear-gradient(135deg, #667eea, #764ba2);
    border-radius: 10px;
    transition: width 0.6s ease;
}

/* Seção de Mentoria */
.mentoring-section {
    background: white;
    border-radius: 15px;
    padding: 1.5rem;
    box-shadow: 0 5px 20px rgba(0,0,0,0.08);
    border: 1px solid rgba(0,0,0,0.05);
}

/* Responsividade */
@media (max-width: 768px) {
    .mentee-profile-section {
        flex-direction: column;
        text-align: center;
        gap: 1.5rem;
    }
    
    .mentee-name {
        font-size: 2rem;
    }
    
    .mentee-contact-info {
        align-items: center;
    }
    
    .mentee-social-links {
        justify-content: center;
    }
    
    .metric-card {
        flex-direction: column;
        text-align: center;
        gap: 0.5rem;
    }
    
    .metric-icon {
        width: 50px;
        height: 50px;
        font-size: 1.2rem;
    }
    
    .metric-content h3 {
        font-size: 1.5rem;
    }
}

@media (max-width: 576px) {
    .mentee-header-card {
        padding: 1rem;
        margin-bottom: 1rem;
    }
    
    .mentee-name {
        font-size: 1.5rem;
    }
    
    .mentee-avatar {
        width: 70px;
        height: 70px;
    }
    
    .progress-section,
    .mentoring-section {
        padding: 1rem;
    }
}

/* Estilos para Formulário no Modal */
.form-details {
    padding: 1rem 0;
}

.form-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e9ecef;
}

.form-questions {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.form-question {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 1.25rem;
    border-left: 4px solid #007bff;
    transition: all 0.3s ease;
}

.form-question:hover {
    background: #e9ecef;
    transform: translateX(5px);
}

.question-header {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
}

.question-number {
    background: #007bff;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    flex-shrink: 0;
}

.question-text {
    font-weight: 500;
    color: #2c3e50;
    line-height: 1.4;
    flex: 1;
}

.question-type {
    margin-bottom: 1rem;
}

.question-alternatives {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid #dee2e6;
}

.alternative-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid #f1f3f4;
    transition: all 0.3s ease;
}

.alternative-item:last-child {
    border-bottom: none;
}

.alternative-item.selected {
    background: #d4edda;
    border-radius: 6px;
    padding: 0.75rem;
    border-left: 3px solid #28a745;
}

.alternative-letter {
    background: #6c757d;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
}

.alternative-item.selected .alternative-letter {
    background: #28a745;
}

.alternative-text {
    color: #495057;
    font-size: 0.9rem;
    flex: 1;
}

.selected-indicator {
    color: #28a745;
    font-size: 1rem;
    margin-left: auto;
}

.question-answer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}

.answer-label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
}

.answer-content {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1rem;
}

.text-answer {
    color: #495057;
    font-style: italic;
    line-height: 1.4;
}

.no-answer {
    color: #dc3545;
    font-style: italic;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.selected-alternatives {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.selected-alternative {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #d4edda;
    border-radius: 6px;
    padding: 0.75rem;
}

.selected-letter {
    background: #28a745;
    color: white;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
}

.selected-text {
    color: #155724;
    font-weight: 500;
    flex: 1;
}

/* Animações */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.mentee-header-card,
.progress-metrics-section,
.progress-section,
.mentoring-section {
    animation: fadeInUp 0.6s ease-out;
}

.metric-card {
    animation: fadeInUp 0.6s ease-out;
}

.metric-card:nth-child(1) { animation-delay: 0.1s; }
.metric-card:nth-child(2) { animation-delay: 0.2s; }
.metric-card:nth-child(3) { animation-delay: 0.3s; }
.metric-card:nth-child(4) { animation-delay: 0.4s; }

/* Modal de Atividade */
.activity-modal-content {
    padding: 1rem 0;
}

.activity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #f8f9fa;
}

.activity-meta {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.activity-type,
.activity-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 600;
}

.type-form {
    background: #e3f2fd;
    color: #1976d2;
}

.type-activity {
    background: #f3e5f5;
    color: #7b1fa2;
}

.type-schedule {
    background: #e8f5e8;
    color: #388e3c;
}

.type-default {
    background: #f5f5f5;
    color: #616161;
}

.status-completed {
    background: #d4edda;
    color: #155724;
}

.status-pending {
    background: #fff3cd;
    color: #856404;
}

.status-waiting {
    background: #d1ecf1;
    color: #0c5460;
}

.activity-date {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #7f8c8d;
    font-size: 0.9rem;
}

.activity-content {
    margin-bottom: 2rem;
}

.activity-content h5 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1rem;
}

.content-body {
    background: #f8f9fa;
    border-radius: 10px;
    padding: 1.5rem;
    border-left: 4px solid #667eea;
}

.content-body p {
    margin: 0;
    line-height: 1.6;
    color: #2c3e50;
}

.no-content {
    text-align: center;
    color: #7f8c8d;
    padding: 2rem;
}

.no-content i {
    font-size: 2rem;
    margin-bottom: 1rem;
    display: block;
}

.activity-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #f8f9fa;
}

.activity-actions .btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 10px;
    font-weight: 600;
    transition: all 0.3s ease;
}

.activity-actions .btn:hover {
    transform: translateY(-2px);
}

/* Responsividade do Modal */
@media (max-width: 768px) {
    .activity-header {
        flex-direction: column;
        gap: 1rem;
    }
    
    .activity-meta {
        justify-content: center;
    }
    
    .activity-actions {
        flex-direction: column;
    }
    
    .activity-actions .btn {
        justify-content: center;
    }
}
</style>
